import { useMutation, useQuery } from '@tanstack/react-query'
import { baseAPICall } from '../../services/baseAPICall'
import { clientInstance } from '../../utils/http-client'
import { queryClient } from '../../services/queryClient'
import {
  ImportDataRowsFormat,
  ImportPreviewDB,
  ImportPreviewNoData,
} from './importer-types'
import { ChainSearchResult } from 'features/elastic/searchers'

/* -------------
List Import Previews
-------------- */
export async function getImportPreviews(signal?: AbortSignal) {
  return baseAPICall<ImportPreviewNoData[]>(() =>
    clientInstance.get('importengine/previews/v2/', { signal })
  )
}

export const useGetImportPreviews = (signal?: AbortSignal, disabled = false) =>
  useQuery({
    queryKey: ['import-previews'],
    queryFn: () => getImportPreviews(signal),
    enabled: !disabled,
    refetchOnMount: true,
  })

/* -------------
Create Import Preview
-------------- */
type CreateImportPreviewParams = {
  import_data: {
    data: ImportDataRowsFormat
    raw_data?: string
    name?: string
    skip_matching?: boolean
  }
}

export async function createImportPreview(values: CreateImportPreviewParams) {
  return baseAPICall<ImportPreviewDB>(
    () => clientInstance.post('importengine/previews/v2/', values),
    { successMessage: 'Import preview created successfully' }
  )
}

/* -------------
Get Import Preview
-------------- */
async function getImportPreview(id: number | null, signal?: AbortSignal) {
  if (!id) return null
  return baseAPICall<ImportPreviewDB>(() =>
    clientInstance.get(`importengine/previews/v2/${id}/`, { signal })
  )
}

// Standalone function for fetching preview (used by XState machine)
export async function fetchImportPreviewData(importNumber: number) {
  return baseAPICall<ImportPreviewDB>(() =>
    clientInstance.get(`importengine/previews/v2/${importNumber}/`)
  )
}

export const useGetImportPreview = (
  importNumber: number | null,
  signal?: AbortSignal
) =>
  useQuery({
    queryKey: ['import-preview', importNumber],
    queryFn: () => getImportPreview(importNumber, signal),
    enabled: !!importNumber,
  })

/* -------------
Update Import Preview
-------------- */
type UpdateImportPreviewParams = CreateImportPreviewParams

export async function updateImportPreview(
  importNumber: number,
  values: Partial<UpdateImportPreviewParams>
) {
  return baseAPICall<number>(() =>
    clientInstance.patch(`importengine/previews/v2/${importNumber}/`, values)
  )
}

/* -------------
Delete Import Preview
-------------- */
async function deleteImportPreview(importNumber: number) {
  return baseAPICall(
    () => clientInstance.delete(`importengine/previews/v2/${importNumber}/`),
    { successMessage: 'Import preview deleted successfully' }
  )
}

export const useDeleteImportPreview = () => {
  return useMutation({
    mutationFn: deleteImportPreview,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-previews'] })
    },
  })
}

/* -------------
Search Chains
-------------- */
async function searchChains(query: string, signal?: AbortSignal) {
  return baseAPICall<ChainSearchResult[]>(() =>
    clientInstance.get('importengine/chains/search/', {
      signal,
      params: { q: query },
    })
  )
}

export const useSearchChains = (query: string, signal?: AbortSignal) =>
  useQuery({
    queryKey: ['chains-search', query],
    queryFn: () => searchChains(query, signal),
    enabled: !!query,
  })

/* -------------
Validate Deal Account Owners
-------------- */
export interface ValidateDealAccountOwnersResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export async function validateDealAccountOwners(importNumber: number) {
  return baseAPICall<ValidateDealAccountOwnersResponse>(() =>
    clientInstance.get(
      `importengine/previews/v2/${importNumber}/validate-deal-account-owners/`
    )
  )
}

/* -------------
Validate Deal Sales Stages
-------------- */
export interface ValidateDealSalesStagesResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export async function validateDealSalesStages(importNumber: number) {
  return baseAPICall<ValidateDealSalesStagesResponse>(() =>
    clientInstance.get(
      `importengine/previews/v2/${importNumber}/validate-deal-sales-stages/`
    )
  )
}

/* -------------
Validate Deal Products
-------------- */
export interface ValidateDealProductsResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export async function validateDealProducts(importNumber: number) {
  return baseAPICall<ValidateDealProductsResponse>(() =>
    clientInstance.get(
      `importengine/previews/v2/${importNumber}/validate-deal-products/`
    )
  )
}

/* -------------
Validate Deal Product Variants
-------------- */
export interface ValidateDealProductVariantsResponse {
  success: boolean
  missing: string[]
  found: string[]
}

export async function validateDealProductVariants(importNumber: number) {
  return baseAPICall<ValidateDealProductVariantsResponse>(() =>
    clientInstance.get(
      `importengine/previews/v2/${importNumber}/validate-deal-product-variants/`
    )
  )
}

/* -------------
Set Companies
-------------- */
export async function performSetCompanies(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.post(
      `importengine/previews/v2/${importNumber}/set-companies/`
    )
  )
}

/* -------------
Set Tags
-------------- */
export async function performSetTags(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.post(`importengine/previews/v2/${importNumber}/set-tags/`)
  )
}

/* -------------
Set Deals
-------------- */
export async function performSetDeals(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.post(`importengine/previews/v2/${importNumber}/set-deals/`)
  )
}

/* -------------
Set Contacts
-------------- */
export async function performSetContacts(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.post(
      `importengine/previews/v2/${importNumber}/set-contacts/`
    )
  )
}

/* -------------
Set Company Notes
-------------- */
export async function performSetCompanyNotes(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.post(
      `importengine/previews/v2/${importNumber}/set-company-notes/`
    )
  )
}

/* -------------
Poll Status
-------------- */
export async function pollStatus(importNumber: number | null) {
  if (!importNumber) return null
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.get(`importengine/previews/v2/${importNumber}/status/`)
  )
}

// Standalone function for fetching status (used by XState machine)
export async function fetchImportPreviewStatus(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.get(`importengine/previews/v2/${importNumber}/status/`)
  )
}

export const usePollStatus = (
  importNumber: number | null,
  shouldPoll: boolean
) =>
  useQuery({
    queryKey: ['import-preview-status', importNumber],
    queryFn: () => pollStatus(importNumber),
    enabled: !!importNumber && shouldPoll,
    refetchInterval: shouldPoll ? 5000 : false,
  })

/* -------------
Get Unmatched Chains
-------------- */
export async function getUnmatchedChainsPreview() {
  return baseAPICall<{ import_number: number }>(() =>
    clientInstance.get('importengine/previews/v2/unmatched-chains/')
  )
}

/* -------------
Set Unmatched Chains
-------------- */
export async function setUnmatchedChains(importNumber: number) {
  return baseAPICall<ImportPreviewNoData>(() =>
    clientInstance.post(
      `importengine/previews/v2/${importNumber}/merge-unmatched-chains/`
    )
  )
}
